// The single Yoco connection UI.
//
// This replaces three divergent copies — one inside AddBankingPayments, one
// inside EditBankingPayments, and a third entry point on the BankingPayments
// list — which disagreed about both the connection state and how to navigate.
// The two modal copies were also declared inside their parent component's
// body, so React treated them as a new component type on every parent render
// and reset their state each time.
//
// Connection state is a three-way value from the backend. "Connected" now means
// the practice can actually call Yoco, not merely that a token was stored once.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  getYocoAuthorizeUrl,
  getYocoStatus,
  createYocoWebposDevice,
  listYocoWebposDevices,
} from "../../afinanceapi";

export const YOCO_STATE = {
  CONNECTED: "CONNECTED",
  NEEDS_RECONNECT: "NEEDS_RECONNECT",
  NOT_CONNECTED: "NOT_CONNECTED",
  UNKNOWN: "UNKNOWN",
};

/** Shared status pill, so the list and the editor never describe the same state differently. */
export function YocoStatusBadge({ state, loading }) {
  if (loading) return <span className="text-muted">Checking…</span>;
  switch (state) {
    case YOCO_STATE.CONNECTED:
      return <span className="text-success">Yoco: Connected</span>;
    case YOCO_STATE.NEEDS_RECONNECT:
      return <span className="text-warning">Yoco: Reconnect required</span>;
    case YOCO_STATE.NOT_CONNECTED:
      return <span className="text-muted">Yoco: Not connected</span>;
    default:
      return <span className="text-muted">Yoco: Status unavailable</span>;
  }
}

/** Fetch connection state for one practice. Never throws. */
export async function fetchYocoState(practiceNumber) {
  try {
    const res = await getYocoStatus(practiceNumber);
    return res?.state || (res?.connected ? YOCO_STATE.CONNECTED : YOCO_STATE.NOT_CONNECTED);
  } catch {
    return YOCO_STATE.UNKNOWN;
  }
}

/** Fetch Web POS devices for one practice. Never throws. */
export async function fetchYocoDevices(practiceNumber) {
  try {
    return (await listYocoWebposDevices(practiceNumber)) || [];
  } catch {
    return [];
  }
}

export default function YocoConnect({ practiceNumber }) {
  const [state, setState] = useState(YOCO_STATE.UNKNOWN);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const refreshStatus = useCallback(async () => {
    if (!practiceNumber) return;
    setChecking(true);
    setError(null);
    try {
      const res = await getYocoStatus(practiceNumber);
      setState(res?.state || YOCO_STATE.NOT_CONNECTED);
    } catch {
      setState(YOCO_STATE.UNKNOWN);
      setError("Could not check the Yoco connection. Try again in a moment.");
    } finally {
      setChecking(false);
    }
  }, [practiceNumber]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // The OAuth callback posts back to the opener and closes itself, so the
  // status refreshes without a full page reload. Previously the popup landed on
  // a hardcoded host and the opener was never told anything.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== "practosync-yoco") return;
      refreshStatus();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refreshStatus]);

  const handleConnect = async () => {
    if (!practiceNumber) return;
    setError(null);
    try {
      const returnTo = window.location.origin + window.location.pathname + window.location.hash;
      const url = await getYocoAuthorizeUrl(practiceNumber, returnTo);
      const popup = window.open(url, "yoco-oauth", "width=520,height=720");
      if (!popup) {
        // Popup blocked — fall back to a full-page navigation.
        window.location.href = url;
      }
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? "You need SuperAdmin access to connect Yoco for this practice."
          : "Failed to start the Yoco connection."
      );
    }
  };

  const connected = state === YOCO_STATE.CONNECTED;
  const needsReconnect = state === YOCO_STATE.NEEDS_RECONNECT;

  return (
    <>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {needsReconnect && (
        <Alert variant="warning">
          This practice's Yoco authorisation has expired. Reconnect to keep taking
          card payments.
        </Alert>
      )}

      <Row className="mb-2">
        <Form.Label className="text-muted">
          Connect your Yoco account to enable in-store payments (Web POS). This
          opens a secure Yoco page.
        </Form.Label>
      </Row>

      <Row className="align-items-center g-2">
        <Col xs="auto">
          <Button
            variant={connected ? "outline-primary" : "primary"}
            onClick={handleConnect}
            disabled={!practiceNumber}
          >
            {connected || needsReconnect ? "Reconnect Yoco" : "Connect Yoco"}
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-secondary"
            onClick={refreshStatus}
            disabled={checking || !practiceNumber}
          >
            {checking ? "Checking…" : "Check status"}
          </Button>
        </Col>
        <Col>
          <YocoStatusBadge state={state} loading={checking} />
        </Col>
      </Row>

      {connected && <WebPosDeviceCreator practiceNumber={practiceNumber} />}
    </>
  );
}

export function WebPosDeviceCreator({ practiceNumber }) {
  const [name, setName] = useState("Front Desk POS");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [lastDevice, setLastDevice] = useState(null);
  const [devices, setDevices] = useState([]);

  const loadDevices = useCallback(async () => {
    if (!practiceNumber) return;
    try {
      setDevices(await listYocoWebposDevices(practiceNumber));
    } catch {
      setDevices([]);
    }
  }, [practiceNumber]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleCreate = async () => {
    if (!practiceNumber || !name?.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const device = await createYocoWebposDevice(practiceNumber, name.trim());
      setLastDevice(device);
      await loadDevices();
    } catch (e) {
      setError(
        e?.response?.data?.error || "Could not create the device. Try again."
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <hr />
      <Row className="mb-2">
        <Form.Label className="fw-semibold">Web POS devices</Form.Label>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-3 g-2">
        <Col md={7}>
          <Form.Control
            placeholder="Device name (e.g. Reception POS)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Col>
        <Col md="auto">
          <Button onClick={handleCreate} disabled={creating || !practiceNumber}>
            {creating ? (
              <Spinner size="sm" animation="border" />
            ) : (
              "Create Web POS device"
            )}
          </Button>
        </Col>
      </Row>

      {devices.length > 0 && (
        <Row className="mb-2">
          <Col>
            <div className="small text-muted">Existing devices:</div>
            <ul className="small mb-0">
              {devices.map((d) => (
                <li key={d.id}>
                  <code>{d.name}</code> — <span className="text-muted">{d.id}</span>
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      )}

      {lastDevice && (
        <Alert variant="success" className="mt-2">
          Created device <strong>{lastDevice.name}</strong> —{" "}
          <code>{lastDevice.id}</code>
        </Alert>
      )}
    </>
  );
}
